import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { logActivity } from "../../utils/activity";
import ComboInput from "../../components/ComboInput/ComboInput";
import { todayStr } from "../../utils/dateRange";
import { WEEKDAY_NAMES, formatWeekday, formatWeekdayDate } from "../../utils/format";

const GAMES_CLOUD_BASE = (process.env.REACT_APP_GAMES_CLOUD_URL || "")
    .replace(/^ws/, "http")
    .replace(/ws$/, "");

const LIST_LABELS = { open: "Åbneliste", day: "Todo i dag", close: "Lukkeliste" };
// Bitmask Mon=bit0..Sun=bit6 — matches the backend's mondayZeroWeekday.
const WEEKDAYS = [
    { label: "Man", bit: 0 }, { label: "Tir", bit: 1 }, { label: "Ons", bit: 2 },
    { label: "Tor", bit: 3 }, { label: "Fre", bit: 4 }, { label: "Lør", bit: 5 },
    { label: "Søn", bit: 6 },
];

function api(path, opts = {}) {
    const token = localStorage.getItem("accessToken");
    return fetch(`${GAMES_CLOUD_BASE}${path}`, {
        ...opts,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
    });
}

const emptyForm = {
    id: null, title: "", category: "", points: 10, repeat_type: "interval",
    interval_days: 1, anchor_date: todayStr(), weekdays: 0, window_days: 0,
};

// The last day a task's occurrence may be done: its start day plus the window.
function addDays(dateStr, n) {
    const d = new Date(`${dateStr}T12:00:00`);
    d.setDate(d.getDate() + n);
    const p = x => String(x).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// Smallest number of days between two selected weekdays (wrapping around the week); a single
// selected day is 7 from itself. A window must be shorter than this, or occurrences overlap.
// Mirrors the backend's minWeekdayGap.
function minWeekdayGap(mask) {
    const days = WEEKDAYS.filter(d => mask & (1 << d.bit)).map(d => d.bit);
    if (days.length === 0) return 0;
    if (days.length === 1) return 7;
    return Math.min(...days.map((d, i) => (days[(i + 1) % days.length] - d + 7) % 7));
}

// formIncomplete: something is simply not filled in yet (title, weekday, date). That only
// keeps the save button off — a half-filled form is not a mistake worth a red line.
function formIncomplete(form) {
    if (!form.title.trim()) return true;
    if (form.repeat_type === "weekdays") return form.weekdays === 0;
    return !form.anchor_date;
}

// formError explains why the form can't be saved as it stands — an illegal choice, such as
// a window that overlaps the next occurrence — or "" when it can. Shown live in red under
// the form (and it disables the save button) rather than as a popup on submit.
function formError(form) {
    const window = Number(form.window_days) || 0;
    if (window < 0) return "Fristen kan ikke være negativ.";
    if (form.repeat_type === "weekdays") {
        if (form.weekdays === 0) return "";
        const gap = minWeekdayGap(form.weekdays);
        if (window >= gap) {
            return gap === 7
                ? "Fristen skal være højst 6 dage, ellers når den ind i næste uges opgave."
                : `Fristen skal være højst ${gap - 1} dage — der er kun ${gap} dage mellem de valgte ugedage, så opgaverne ville overlappe.`;
        }
        return "";
    }
    if (form.repeat_type === "interval") {
        const every = Number(form.interval_days) || 0;
        if (every < 1) return "Gentagelsen skal være mindst 1 dag.";
        if (window >= every) {
            return every === 1
                ? "En opgave der gentages hver dag kan ikke have en frist — næste dags opgave ville overlappe den. Brug fx \"hver 3. dag\" med frist 2 i stedet."
                : `Fristen skal være højst ${every - 1} dage — opgaven gentages hver ${every}. dag, så en længere frist ville overlappe den næste.`;
        }
    }
    return "";
}

// ChecklistEditor is the "Todo-lister" admin tab: per-location task definitions with
// drag-to-reorder and an add/edit form.
export default function ChecklistEditor({ config }) {
    const locationKeys = useMemo(() => Object.keys(config?.locations || {}), [config]);
    const [location, setLocation] = useState("");
    const [listType, setListType] = useState("open");

    const [tasks, setTasks] = useState([]);
    const [form, setForm] = useState(emptyForm);
    // Why the last save failed (network/server), cleared on the next edit or successful save.
    const [saveError, setSaveError] = useState("");
    const error = formError(form);
    const cannotSave = !!error || formIncomplete(form);

    const locationName = key => config?.locations?.[key]?.displayName || key;
    // Existing category names for the datalist suggestions.
    const categories = useMemo(
        () => [...new Set(tasks.map(t => t.category).filter(Boolean))].sort((a, b) => a.localeCompare(b, "da")),
        [tasks]
    );

    // Default to the first location once config arrives.
    useEffect(() => {
        const first = Object.keys(config?.locations || {})[0] || "";
        setLocation(prev => prev || first);
    }, [config]);

    const loadTasks = useCallback(async () => {
        if (!location) return;
        const r = await api(`checklist/tasks?location=${encodeURIComponent(location)}&list_type=${listType}`);
        if (r.ok) setTasks((await r.json()).tasks || []);
    }, [location, listType]);

    useEffect(() => { loadTasks(); }, [loadTasks]);

    // Drag-and-drop reordering. On drop we move the row locally and persist the new order
    // (sort_order = position) in one atomic call.
    const dragIndex = useRef(null);

    async function persistOrder(ordered) {
        const r = await api("checklist/tasks/reorder", {
            method: "POST", body: JSON.stringify({ ids: ordered.map(t => t.id) }),
        });
        if (!r.ok) { alert("Kunne ikke gemme rækkefølge."); loadTasks(); return; }
        logActivity("checklist_task_reorder", `${location}/${listType}`);
    }

    function handleDrop(targetIndex) {
        const from = dragIndex.current;
        dragIndex.current = null;
        if (from === null || from === targetIndex) return;
        const next = [...tasks];
        const [moved] = next.splice(from, 1);
        next.splice(targetIndex, 0, moved);
        setTasks(next);
        persistOrder(next);
    }

    async function saveTask(e) {
        e.preventDefault();
        const existing = tasks.find(t => t.id === form.id);
        const body = {
            location, list_type: listType,
            title: form.title.trim(), category: form.category.trim(), points: Number(form.points) || 0,
            repeat_type: form.repeat_type,
            interval_days: Number(form.interval_days) || 1,
            anchor_date: form.repeat_type === "weekdays" ? "" : form.anchor_date,
            weekdays: form.repeat_type === "weekdays" ? form.weekdays : 0,
            window_days: Number(form.window_days) || 0,
            // New tasks append to the bottom; edits keep their place (order is set via drag).
            sort_order: form.id ? (existing?.sort_order ?? 0) : tasks.length,
            active: true,
        };
        if (cannotSave) return; // the button is disabled, but Enter in a field still submits
        const r = form.id
            ? await api(`checklist/tasks/${form.id}`, { method: "PUT", body: JSON.stringify(body) })
            : await api("checklist/tasks", { method: "POST", body: JSON.stringify(body) });
        if (!r.ok) { setSaveError("Kunne ikke gemme opgaven — prøv igen."); return; }
        setSaveError("");
        logActivity(form.id ? "checklist_task_edit" : "checklist_task_create", `${location}/${listType}/${body.title}`);
        setForm({ ...emptyForm, anchor_date: todayStr() });
        loadTasks();
    }

    function editTask(t) {
        setSaveError("");
        setForm({
            id: t.id, title: t.title, category: t.category || "", points: t.points, repeat_type: t.repeat_type,
            interval_days: t.interval_days || 1, anchor_date: t.anchor_date || todayStr(),
            weekdays: t.weekdays || 0, window_days: t.window_days || 0,
        });
    }

    async function deleteTask(t) {
        if (!window.confirm(`Slet opgaven "${t.title}"?`)) return;
        const r = await api(`checklist/tasks/${t.id}`, { method: "DELETE" });
        if (!r.ok) { alert("Kunne ikke slette."); return; }
        logActivity("checklist_task_delete", `${location}/${t.title}`);
        if (form.id === t.id) setForm({ ...emptyForm, anchor_date: todayStr() });
        loadTasks();
    }

    function toggleWeekday(bit) {
        setForm(f => ({ ...f, weekdays: f.weekdays ^ (1 << bit) }));
    }

    // "Frist": how many days after the start day the task may still be done. 0 means it
    // must be done on the start day itself, like before.
    function renderWindowField() {
        return (
            <label title="0 = skal gøres på selve dagen. 4 = må gøres når som helst fra startdagen og de næste 4 dage.">
                Frist (dage efter start)
                <input type="number" min="0" value={form.window_days}
                    onChange={e => setForm(f => ({ ...f, window_days: e.target.value }))} />
            </label>
        );
    }

    return (
        <>
            <div className="acl-scope">
                <label>Lokation
                    <select value={location} onChange={e => setLocation(e.target.value)}>
                        {locationKeys.map(k => <option key={k} value={k}>{locationName(k)}</option>)}
                    </select>
                </label>
                <label>Liste
                    <select value={listType} onChange={e => setListType(e.target.value)}>
                        <option value="open">{LIST_LABELS.open}</option>
                        <option value="day">{LIST_LABELS.day}</option>
                        <option value="close">{LIST_LABELS.close}</option>
                    </select>
                </label>
            </div>

            <section className="acl-card">
                <h2>Opgaver — {locationName(location)} · {LIST_LABELS[listType]}</h2>
                <p className="acl-hint">Træk i ⠿ for at ændre rækkefølgen opgaverne vises i.</p>
                <table className="acl-table">
                    <thead>
                        <tr><th></th><th>Titel</th><th>Kategori</th><th>Point</th><th>Gentagelse</th><th></th></tr>
                    </thead>
                    <tbody>
                        {tasks.length === 0 && <tr><td colSpan={6} className="acl-dim">Ingen opgaver endnu.</td></tr>}
                        {tasks.map((t, i) => (
                            <tr key={t.id}
                                draggable
                                onDragStart={() => { dragIndex.current = i; }}
                                onDragOver={e => e.preventDefault()}
                                onDrop={() => handleDrop(i)}
                                className="acl-draggable">
                                <td className="acl-handle" title="Træk for at flytte">⠿</td>
                                <td>{t.title}</td>
                                <td className="acl-dim">{t.category || "—"}</td>
                                <td>{t.points}</td>
                                <td className="acl-dim">{describeRepeat(t)}</td>
                                <td className="acl-row-actions">
                                    <button onClick={() => editTask(t)}>Rediger</button>
                                    <button className="danger" onClick={() => deleteTask(t)}>Slet</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <form className="acl-form" onSubmit={saveTask}>
                    <h3>{form.id ? "Rediger opgave" : "Ny opgave"}</h3>
                    <div className="acl-fields">
                        <label className="grow">Titel
                            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
                        </label>
                        <label>Kategori
                            <ComboInput value={form.category} options={categories} placeholder="valgfri"
                                onChange={v => setForm(f => ({ ...f, category: v }))} />
                        </label>
                        <label>Point
                            <input type="number" min="0" value={form.points}
                                onChange={e => setForm(f => ({ ...f, points: e.target.value }))} />
                        </label>
                        <label>Gentagelse
                            <select value={form.repeat_type}
                                onChange={e => setForm(f => ({ ...f, repeat_type: e.target.value }))}>
                                <option value="interval">Hver X dag</option>
                                <option value="weekdays">Bestemte ugedage</option>
                                <option value="once">Kun én gang</option>
                            </select>
                        </label>
                    </div>

                    {form.repeat_type === "once" ? (
                        <div className="acl-fields">
                            <label>Fra dato
                                <input type="date" value={form.anchor_date}
                                    onChange={e => setForm(f => ({ ...f, anchor_date: e.target.value }))} />
                            </label>
                            {renderWindowField()}
                        </div>
                    ) : form.repeat_type === "interval" ? (
                        <div className="acl-fields">
                            <label>Hver … dag
                                <input type="number" min="1" value={form.interval_days}
                                    onChange={e => setForm(f => ({ ...f, interval_days: e.target.value }))} />
                            </label>
                            <label>Startdato
                                <input type="date" value={form.anchor_date}
                                    onChange={e => setForm(f => ({ ...f, anchor_date: e.target.value }))} />
                            </label>
                            {renderWindowField()}
                        </div>
                    ) : (
                        <>
                            <div className="acl-weekdays">
                                {WEEKDAYS.map(d => (
                                    <label key={d.bit} className={form.weekdays & (1 << d.bit) ? "on" : ""}>
                                        <input type="checkbox" checked={!!(form.weekdays & (1 << d.bit))}
                                            onChange={() => toggleWeekday(d.bit)} />
                                        {d.label}
                                    </label>
                                ))}
                            </div>
                            <div className="acl-fields">{renderWindowField()}</div>
                        </>
                    )}

                    {Number(form.window_days) > 0 && (
                        <p className="acl-hint">{describeWindowHint(form)}</p>
                    )}

                    {(error || saveError) && (
                        <p className="acl-error" role="alert">{error || saveError}</p>
                    )}

                    <div className="acl-form-actions">
                        <button type="submit" className="accent" disabled={cannotSave}>
                            {form.id ? "Gem ændringer" : "Tilføj opgave"}
                        </button>
                        {form.id && (
                            <button type="button" onClick={() => setForm({ ...emptyForm, anchor_date: todayStr() })}>
                                Annullér
                            </button>
                        )}
                    </div>
                </form>
            </section>
        </>
    );
}

// A plain-language preview of the window while the admin fills in the form, e.g.
// "Må gøres man.–fre. (4 dage) — næste frist fre. 25. sep."
function describeWindowHint(form) {
    const n = Number(form.window_days) || 0;
    if (n <= 0) return "";
    if (form.repeat_type === "weekdays") {
        const spans = weekdaySpans(form.weekdays, n);
        if (!spans) return "";
        return `Skal gøres én gang pr. valgt ugedag, når som helst ${spans} (frist ${n} dage efter startdagen).`;
    }
    if (!form.anchor_date) return "";
    const deadline = addDays(form.anchor_date, n);
    const span = `${formatWeekday(form.anchor_date)}–${formatWeekday(deadline)}`;
    if (form.repeat_type === "once") {
        return `Skal gøres én gang i perioden ${span}, senest ${formatWeekdayDate(deadline)}.`;
    }
    const every = Number(form.interval_days) || 1;
    const cadence = every % 7 === 0
        ? (every === 7 ? "hver uge" : `hver ${every / 7}. uge`)
        : `hver ${every}. dag`;
    return `Skal gøres én gang ${cadence}, når som helst ${span} (frist ${n} dage efter startdagen).`;
}

// "man.–fre." for each selected weekday and its window, joined with commas.
function weekdaySpans(mask, n) {
    return WEEKDAYS.filter(d => mask & (1 << d.bit))
        .map(d => `${WEEKDAY_NAMES[d.bit]}–${WEEKDAY_NAMES[(d.bit + n) % 7]}`)
        .join(", ");
}

function describeRepeat(t) {
    // A task with a window is done once somewhere in "start–deadline" rather than on one day.
    const win = t.window_days > 0 && t.anchor_date
        ? ` · ${formatWeekday(t.anchor_date)}–${formatWeekday(addDays(t.anchor_date, t.window_days))} (frist ${t.window_days} dage)`
        : "";
    if (t.repeat_type === "once") {
        return `Én gang · ${t.anchor_date || "—"}${win}`;
    }
    if (t.repeat_type === "weekdays") {
        if (t.window_days > 0) {
            return `${weekdaySpans(t.weekdays, t.window_days)} (frist ${t.window_days} dage)`;
        }
        const days = WEEKDAYS.filter(d => t.weekdays & (1 << d.bit)).map(d => d.label);
        return days.length ? days.join(", ") : "—";
    }
    return (t.interval_days === 1 ? "Hver dag" : `Hver ${t.interval_days}. dag`) + win;
}
